import React, { useState, useEffect, useContext, useMemo } from 'react';
import { Offcanvas, Form, Row, Col, InputGroup, Button, ButtonGroup } from 'react-bootstrap';
import Preloader from '../../../components/Preloader/Preloader';
import { ProductContext } from '../../../ProductProvider';
import { useSnackbar } from 'notistack';
import { fetchAuthSession } from 'aws-amplify/auth';
import axios from 'axios';
import JournalCardView from './JournalCardView';
import JournalTablePaginationView from './JournalTablePaginationView';
import JournalOffcanvas from './JournalOffcanvas';
import ArticlesTablePaginationView from './ArticlesTablePaginationView';
import ArticlesOffcanvas from './ArticlesOffcanvas';
import '../SearchMain.css';

const config = require('../../../config.json');

export default function JournalSearch(props) {
  const { initialQuery, initialTopic } = props;
  const { validateLogin, loggedUser } = useContext(ProductContext);
  const { enqueueSnackbar } = useSnackbar();

  const [searchResult, setSearchResult] = useState([]);
  const [searchValue, setSearchValue] = useState(initialQuery || '');
  const [searchTopic, setSearchTopic] = useState(initialTopic || 'JournalTitle');
  const [loaded, setLoaded] = useState(true);
  const [searchCompleted, setSearchCompleted] = useState(false);
  const [dispMsg, setDispMsg] = useState('');
  const [viewMode, setViewMode] = useState('grid'); // 'grid' | 'table'
  const [clientFilter, setClientFilter] = useState('');

  // Offcanvas drawer state
  const [showDrawer, setShowDrawer] = useState(false);
  const [selectedItem, setSelectedItem] = useState(null);

  useEffect(() => {
    if (!loggedUser.isAuthenticated) {
      validateLogin();
    }
  }, [loggedUser]);

  // Handle external topic clicks or initial search triggers
  useEffect(() => {
    if (initialQuery) {
      //console.log("initial Query :", initialQuery)
      //console.log("initial Topic :", initialTopic);
      setSearchValue(initialQuery);
      const chosenTopic = initialTopic || 'JournalTitle';
      setSearchTopic(chosenTopic);
      executeSearch(initialQuery, chosenTopic);
    }
  }, [initialQuery, initialTopic]);

  const handleCloseDrawer = () => setShowDrawer(false);

  const handleShowDrawer = (event, item) => {
    if (event) event.preventDefault();
    setSelectedItem(item);
    setShowDrawer(true);
  };

  const executeSearch = async (val, topic) => {
    const q = (val !== undefined ? val : searchValue).trim();
    if (!q) {
      enqueueSnackbar('Please enter a search term for periodicals or articles', { variant: 'info' });
      return;
    }

    try {
      setLoaded(false);
      setDispMsg('');
      setSearchCompleted(false);

      const { idToken } = (await fetchAuthSession()).tokens ?? {};
      const clientID = 'VJCollege';
      const currentTopic = topic || searchTopic;
      const keysearchValue = q.toUpperCase();

      let apiURL = `${config.api.invokeURL}/vjlibrary/search/journal`;
      let successLabel = 'Periodicals';

      if (currentTopic.startsWith('Article')) {
        apiURL = `${config.api.invokeURL}/vjlibrary/search/journal/articles`;
        successLabel = 'Articles';
      }
      if (currentTopic.startsWith('KeywordsArticle')) {
        apiURL = `${config.api.invokeURL}/vjlibrary/search/journal/articles/keyword`;
        successLabel = 'Articles';
      }

      const response = await axios.get(apiURL, {
        params: {
          fetchKey: keysearchValue,
          fetchOption: currentTopic,
          clientID: clientID
        },
        headers: {
          Authorization: idToken,
          'x-api-key': config.api.key
        }
      });
      //console.log("Res:", response);
      const data = Array.isArray(response.data) ? response.data : [];
      setSearchResult(data);
      setDispMsg(`${data.length} ${successLabel} Found`);
      enqueueSnackbar(`${data.length} ${successLabel} Found`, { variant: 'success' });
      setSearchCompleted(true);
      setLoaded(true);
    } catch (error) {
      console.error('Error searching journals/articles:', error);
      setLoaded(true);
      enqueueSnackbar('Error occurred: ' + (error.response?.data?.message || error.message), { variant: 'warning' });
    }
  };

  const handleFormSubmit = (e) => {
    e.preventDefault();
    executeSearch();
  };

  // Real-time In-page Filter
  const filteredResults = useMemo(() => {
    if (!clientFilter.trim()) return searchResult;
    const term = clientFilter.toLowerCase();
    return searchResult.filter((item) => {
      const title = (item.journalTitle || item.articleTitle || item.bookTitle || '').toLowerCase();
      const author = (item.articleAuthor || item.publisher || '').toLowerCase();
      const id = (item.journalID || item.articleID || '').toString().toLowerCase();
      const keywords = (item.journalKeywords || item.articleKeywords || '').toLowerCase();
      const subject = (item.journalSubject || '').toLowerCase();
      const loc = (item.liblocation || '').toLowerCase();
      const callNo = (item.callNo || item.journalCallNo || item.bookClassificationNo || item.classificationNo || '').toLowerCase();
      const vol = (item.volissuesNo || '').toString().toLowerCase();
      const journalType = (item.journalType || '').toLowerCase();

      return (
        title.includes(term) ||
        author.includes(term) ||
        id.includes(term) ||
        keywords.includes(term) ||
        subject.includes(term) ||
        loc.includes(term) ||
        callNo.includes(term) ||
        vol.includes(term) ||
        journalType.includes(term)
      );
    });
  }, [searchResult, clientFilter]);

  const boundJournalCount = useMemo(() => {
    return searchResult.filter(
      (item) => (item.journalType || '').trim().toLowerCase() === 'bound journal'
    ).length;
  }, [searchResult]);

  const isArticleSearch = searchTopic.startsWith('Article') || searchTopic.startsWith('KeywordsArticle');

  return (
    <div>
      {!loaded && <Preloader show={true} />}

      {/* Journal / Article Search Form Card */}
      <div className="vj-search-box-card">
        <Form onSubmit={handleFormSubmit}>
          <Row className="g-3 align-items-center">
            <Col lg={3} md={4} sm={12}>
              <Form.Select
                className="vj-search-select w-100"
                value={searchTopic}
                onChange={(e) => setSearchTopic(e.target.value)}
              >
                <option value="JournalTitle">📰 Journal Title</option>
                <option value="JournalID">🔢 Journal ID</option>
                <option value="ArticleTitle">📄 Article Title</option>
                <option value="ArticleAuthor">✍️ Article Author</option>
                <option value="KeywordsArticle">🏷️ Keywords / Subject</option>
              </Form.Select>
            </Col>

            <Col lg={6} md={5} sm={12}>
              <InputGroup>
                <InputGroup.Text className="bg-light border-end-0">
                  <i className="bi bi-search text-muted"></i>
                </InputGroup.Text>
                <Form.Control
                  type="text"
                  className="vj-search-input border-start-0"
                  placeholder={`Search by ${searchTopic.replace(/([A-Z])/g, ' $1').trim()}...`}
                  value={searchValue}
                  onChange={(e) => setSearchValue(e.target.value)}
                />
                {searchValue && (
                  <Button variant="outline-secondary" onClick={() => setSearchValue('')}>
                    <i className="bi bi-x"></i>
                  </Button>
                )}
              </InputGroup>
            </Col>

            <Col lg={3} md={3} sm={12}>
              <button type="submit" className="btn-vj-search">
                <i className="bi bi-search"></i> Search Periodicals
              </button>
            </Col>
          </Row>
        </Form>
      </div>

      {/* Results Section */}
      {searchCompleted && (
        <div className="container px-0">
          {/* Results Toolbar */}
          <div className="vj-results-toolbar">
            <div className="d-flex align-items-center gap-3 flex-wrap">
              <span className="vj-results-count">
                <i className="bi bi-journal-text text-primary me-2"></i>
                {dispMsg}
              </span>
              {searchResult.length > 0 && (
                <span className="badge bg-light text-muted border px-2 py-1">
                  Showing {filteredResults.length}
                </span>
              )}
              {boundJournalCount > 0 && (
                <button
                  type="button"
                  className={`btn btn-sm ${
                    clientFilter.toLowerCase() === 'bound journal'
                      ? 'btn-primary'
                      : 'badge-bound-journal-subtle'
                  } d-inline-flex align-items-center gap-1 border`}
                  onClick={() =>
                    setClientFilter((prev) =>
                      prev.toLowerCase() === 'bound journal' ? '' : 'Bound Journal'
                    )
                  }
                  title="Click to filter Bound Journals"
                  style={{ cursor: 'pointer' }}
                >
                  <i className="bi bi-journal-bookmark-fill"></i>
                  <span>Bound Journals</span>
                  <span className="badge bg-white text-dark ms-1 shadow-sm">
                    {boundJournalCount}
                  </span>
                </button>
              )}
            </div>

            {searchResult.length > 0 && (
              <div className="d-flex align-items-center gap-2">
                {/* Instant In-page Filter */}
                <div style={{ maxWidth: '240px' }}>
                  <InputGroup size="sm">
                    <InputGroup.Text className="bg-white">
                      <i className="bi bi-funnel text-muted"></i>
                    </InputGroup.Text>
                    <Form.Control
                      type="text"
                      placeholder="Filter results..."
                      value={clientFilter}
                      onChange={(e) => setClientFilter(e.target.value)}
                    />
                    {clientFilter && (
                      <Button variant="outline-secondary" onClick={() => setClientFilter('')}>
                        <i className="bi bi-x"></i>
                      </Button>
                    )}
                  </InputGroup>
                </div>

                {/* View Mode Switcher */}
                <ButtonGroup size="sm">
                  <Button
                    variant={viewMode === 'grid' ? 'primary' : 'outline-secondary'}
                    onClick={() => setViewMode('grid')}
                    title="Grid Cards View"
                  >
                    <i className="bi bi-grid-fill"></i>
                  </Button>
                  <Button
                    variant={viewMode === 'table' ? 'primary' : 'outline-secondary'}
                    onClick={() => setViewMode('table')}
                    title="Table List View"
                  >
                    <i className="bi bi-table"></i>
                  </Button>
                </ButtonGroup>
              </div>
            )}
          </div>

          {/* Results Display */}
          {searchResult.length === 0 ? (
            <div className="text-center py-5 bg-white rounded-3 border p-4">
              <i className="bi bi-search text-muted fs-1 mb-2 d-block"></i>
              <h5>No Periodicals or Articles Found</h5>
              <p className="text-muted small mb-0">
                Try searching with a different keyword, check spelling, or search by Journal Title.
              </p>
            </div>
          ) : viewMode === 'grid' ? (
            <JournalCardView
              searchResult={filteredResults}
              handleShow={handleShowDrawer}
              isArticle={isArticleSearch}
            />
          ) : isArticleSearch ? (
            <ArticlesTablePaginationView
              searchResult={filteredResults}
              handleShow={handleShowDrawer}
            />
          ) : (
            <JournalTablePaginationView
              searchResult={filteredResults}
              handleShow={handleShowDrawer}
            />
          )}
        </div>
      )}

      {/* Offcanvas Drawer */}
      <Offcanvas
        show={showDrawer}
        onHide={handleCloseDrawer}
        placement="end"
        scroll={true}
        style={{ width: isArticleSearch ? '480px' : '600px', maxWidth: '95vw' }}
      >
        <Offcanvas.Header closeButton className="vj-drawer-header">
          <Offcanvas.Title className="vj-drawer-title">
            <i
              className={`bi ${
                isArticleSearch
                  ? 'bi-file-earmark-text'
                  : (selectedItem?.journalType || '').trim().toLowerCase() === 'bound journal'
                  ? 'bi-journal-bookmark-fill'
                  : 'bi-journal-album'
              } me-2`}
            ></i>
            {isArticleSearch
              ? 'Article Details'
              : (selectedItem?.journalType || '').trim().toLowerCase() === 'bound journal'
              ? 'Bound Journal Details'
              : 'Journal Details'}
          </Offcanvas.Title>
        </Offcanvas.Header>
        <Offcanvas.Body className="p-3">
          {selectedItem &&
            (isArticleSearch ? (
              <ArticlesOffcanvas selectedBook={selectedItem} />
            ) : (
              <JournalOffcanvas selectedBook={selectedItem} />
            ))}
        </Offcanvas.Body>
      </Offcanvas>
    </div>
  );
}
